/**
 * DateTextInput Component
 * 
 * Specialized TextInput for date inputs that supports:
 * - ISO format (full date/time) - 'iso' variant
 * - Simple DD/MM/YYYY format - 'simple' variant (default)
 * 
 * Features:
 * - Inline calendar datepicker popup (portaled to avoid overflow clipping)
 * - Automatic input masking for DD/MM/YYYY format with cursor position management
 * - Input validation and formatting
 */

import React, { forwardRef, useCallback, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from '@core/icons-react';
import { TextInput } from '../TextInput/TextInput';
import type { TextInputProps } from '../TextInput/TextInput';
import './DateTextInput.scss';

export interface DateTextInputProps extends Omit<TextInputProps, 'type' | 'placeholder'> {
  /**
   * Date input variant
   * - 'simple': DD/MM/YYYY format with brackets (default)
   * - 'iso': Full ISO date/time format
   */
  variant?: 'simple' | 'iso';
  
  /**
   * Optional custom placeholder (overrides default)
   */
  placeholder?: string;

  /**
   * Disable the calendar popup (text-only input)
   */
  noCalendar?: boolean;
}

// --- Pure helpers ---

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const formatSimpleInput = (input: string): string => {
  const digits = input.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

const isoToSimpleDate = (isoDate: string): string => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '';
  }
};

const simpleDateToDate = (simple: string): Date | null => {
  const m = simple.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  if (isNaN(d.getTime())) return null;
  return d;
};

const dateToSimple = (d: Date): string => {
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const computeCursorPosition = (rawValue: string, cursorPos: number, formatted: string): number => {
  const rawBeforeCursor = rawValue.slice(0, cursorPos);
  const digitsBefore = rawBeforeCursor.replace(/\D/g, '').length;
  let digitCount = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      digitCount++;
      if (digitCount === digitsBefore) return i + 1;
    }
  }
  return formatted.length;
};

// --- Calendar sub-component (portaled) ---

interface CalendarPopupProps {
  selectedDate: Date | null;
  onSelect: (date: Date) => void;
  onClose: () => void;
  anchorRect: DOMRect | null;
}

function CalendarPopup({ selectedDate, onSelect, onClose, anchorRect }: CalendarPopupProps) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(selectedDate?.getFullYear() ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate?.getMonth() ?? today.getMonth());
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        const target = e.target as HTMLElement;
        if (target.closest('.core-date-text-input')) return;
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const firstDay = new Date(viewYear, viewMonth, 1);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const startOffset = firstDay.getDay();

  const days: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  while (days.length % 7 !== 0) days.push(null);

  const isSameDay = (d: number) => {
    if (!selectedDate) return false;
    return selectedDate.getFullYear() === viewYear
      && selectedDate.getMonth() === viewMonth
      && selectedDate.getDate() === d;
  };

  const isToday = (d: number) =>
    today.getFullYear() === viewYear
    && today.getMonth() === viewMonth
    && today.getDate() === d;

  // Position the popup below the anchor element using fixed positioning
  const style: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9999,
  };
  
  if (anchorRect) {
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const popupHeight = 280; // approximate height
    
    if (spaceBelow < popupHeight && anchorRect.top > popupHeight) {
      // Open upward if not enough space below
      style.bottom = `${window.innerHeight - anchorRect.top + 4}px`;
    } else {
      style.top = `${anchorRect.bottom + 4}px`;
    }
    style.left = `${anchorRect.left}px`;
  }

  const popup = (
    <div ref={popupRef} className="core-date-text-input__popup" style={style}>
      <div className="core-date-text-input__popup-header">
        <button type="button" className="core-date-text-input__nav-btn" onClick={prevMonth} aria-label="Previous month">
          <ChevronLeft size={16} />
        </button>
        <span className="core-date-text-input__month-title">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button type="button" className="core-date-text-input__nav-btn" onClick={nextMonth} aria-label="Next month">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="core-date-text-input__weekdays">
        {DAY_LABELS.map(d => (
          <div key={d} className="core-date-text-input__weekday">{d}</div>
        ))}
      </div>
      <div className="core-date-text-input__days">
        {days.map((day, i) => {
          if (day === null) return <div key={`e${i}`} className="core-date-text-input__day--empty" />;
          const selected = isSameDay(day);
          const todayClass = isToday(day);
          return (
            <button
              key={day}
              type="button"
              className={[
                'core-date-text-input__day',
                selected && 'core-date-text-input__day--selected',
                todayClass && !selected && 'core-date-text-input__day--today',
              ].filter(Boolean).join(' ')}
              onClick={() => onSelect(new Date(viewYear, viewMonth, day))}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );

  return createPortal(popup, document.body);
}

// --- Main component ---

export const DateTextInput = forwardRef<HTMLInputElement, DateTextInputProps>(function DateTextInput(
  {
    variant = 'simple',
    placeholder,
    value,
    onChange,
    disabled,
    noCalendar,
    ...rest
  },
  ref
) {
  const internalRef = useRef<HTMLInputElement>(null);
  const cursorRef = useRef<number | null>(null);
  const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  // Restore cursor after re-render
  useEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      const pos = cursorRef.current;
      inputRef.current.setSelectionRange(pos, pos);
      cursorRef.current = null;
    }
  });

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (variant === 'simple') {
      const rawValue = event.target.value;
      const cursorPos = event.target.selectionStart ?? rawValue.length;
      const formatted = formatSimpleInput(rawValue);
      cursorRef.current = computeCursorPosition(rawValue, cursorPos, formatted);

      if (onChange) {
        const target = event.target;
        Object.defineProperty(event, 'target', {
          writable: true,
          value: { ...target, value: formatted },
        });
        onChange(event as React.ChangeEvent<HTMLInputElement>);
      }
    } else {
      onChange?.(event);
    }
  }, [variant, onChange]);

  const openCalendar = useCallback(() => {
    if (!calendarOpen && wrapperRef.current) {
      setAnchorRect(wrapperRef.current.getBoundingClientRect());
      setCalendarOpen(true);
    }
  }, [calendarOpen]);

  const handleCalendarSelect = useCallback((date: Date) => {
    const formatted = dateToSimple(date);
    setCalendarOpen(false);

    if (onChange) {
      const fakeEvent = {
        target: { value: formatted },
        currentTarget: { value: formatted },
        preventDefault: () => {},
        stopPropagation: () => {},
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(fakeEvent);
    }
  }, [onChange]);

  const getPlaceholder = (): string => {
    if (placeholder) return placeholder;
    return variant === 'simple' ? 'DD/MM/YYYY' : 'YYYY-MM-DDTHH:mm:ss.sssZ';
  };

  const getDisplayValue = (): string => {
    if (variant === 'simple') {
      if (value !== undefined) {
        const strValue = String(value);
        if (strValue.includes('-')) return isoToSimpleDate(strValue);
        return strValue;
      }
      return '';
    }
    return value ? String(value) : '';
  };

  const showCalendar = variant === 'simple' && !noCalendar && !disabled;
  const displayValue = getDisplayValue();
  const selectedDate = displayValue ? simpleDateToDate(displayValue) : null;

  return (
    <div ref={wrapperRef} className="core-date-text-input" onClick={showCalendar ? openCalendar : undefined}>
      <TextInput
        ref={inputRef}
        type="text"
        placeholder={getPlaceholder()}
        value={displayValue}
        onChange={handleChange}
        maxLength={variant === 'simple' ? 10 : undefined}
        disabled={disabled}
        {...rest}
      />
      {calendarOpen && showCalendar && (
        <CalendarPopup
          selectedDate={selectedDate}
          onSelect={handleCalendarSelect}
          onClose={() => setCalendarOpen(false)}
          anchorRect={anchorRect}
        />
      )}
    </div>
  );
});

DateTextInput.displayName = 'DateTextInput';
